/**
 * Utility para manejar reintentos
 * y clasificación inteligente de errores
 */

/**
 * Espera un tiempo determinado
 */
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Ejecuta una función con reintentos
 */
export async function executeWithRetry(
  asyncFunction,
  options = {},
) {
  const {
    maxAttempts = 5,
    onAttempt = null,
    baseDelay = 500,
    maxDelay = 5000,
    enableJitter = true,
    abortSignal = null,
  } = options;

  let lastError;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    // Verificar si la operación fue cancelada
    if (abortSignal?.aborted) {
      throw new Error('Operation aborted');
    }

    try {
      onAttempt?.(attempt + 1, maxAttempts);
      if (process?.env?.NODE_ENV === 'development') {
        console.debug(`Attempt ${attempt + 1} of ${maxAttempts}`);
      }
      return await asyncFunction();

    } catch (error) {
      lastError = error;
      if (process?.env?.NODE_ENV === 'development') {
        console.error(`Attempt ${attempt + 1} of ${maxAttempts} failed:`, error);
      }
      // Si no es el último intento, esperar antes del siguiente
      if (attempt < maxAttempts - 1) {
        const delay = calculateBackoffDelay(attempt, baseDelay, maxDelay, enableJitter);
        await sleep(delay);
      }
    }
  }

  // Si llegamos aquí, todos los intentos fallaron
  throw lastError;
}

/**
 * Calcula el tiempo de espera
 */
function calculateBackoffDelay(attempt, baseDelay = 1000, maxDelay = 5000, enableJitter = true) {
  const delay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);

  if (!enableJitter) {
    return delay;
  }

  const jitter = delay * 0.25 * (Math.random() - 0.5);
  return Math.round(delay + jitter);
}

/**
 * Mapea errores a mensajes para el usuario
 * Recibe la función de traducción t como argumento
 */
export function getErrorMessage(error, t) {
  const errorMessages = {
    CodeMismatchException: t('error.CodeMismatchException'),
    ExpiredCodeException: t('error.ExpiredCodeException'),
    UserNotConfirmedException: 'Se requiere verificar al usuario',
    CoreErrorAddWebClient: 'Error al registrar el cliente',
    CoreErrorNotPeopleId: 'Error en la configuración del usuario',
    NetworkError: 'Ha ocurrido un error de red. Por favor recarga la página.',
    LimitExceededException: t('error.LimitExceededException'),
    'Request failed with status code 409': t('error.UserExistsException'),
    'Request failed with status code 500': 'Error temporal del servidor. Inténtalo de nuevo.',
    'Request failed with status code 502': 'Servicio no disponible temporalmente',
    'Request failed with status code 503': 'Servicio en mantenimiento. Inténtalo más tarde.',
    'Request failed with status code 504': 'Tiempo de espera agotado. Inténtalo de nuevo.',
  };

  const errorMessage = error?.message || '';
  const errorCode = error?.code || '';

  // Buscar mensaje específico
  for (const [key, message] of Object.entries(errorMessages)) {
    if (errorMessage.includes(key) || errorCode === key) {
      return message;
    }
  }

  // Mensaje genérico para errores desconocidos
  return t('error.UnknownError');
}
